import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getLeadNotes, getPatientNotes } from "../services/api.js";
import { formatDate } from "../utils";
import { formatNoteContent } from "../utils/displayFormatters";
import NoteCreate from "./NoteCreate";

const LeadNotes = ({ authToken, id: propId, onNoteSelect, onExpandRequest, showFirstOnly = false, showCreateForm = false, onNoteFormClose, onCountChange }) => {
  const { id: urlId } = useParams();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showNoteCreate, setShowNoteCreate] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(new Set());

  const effectiveId = propId || urlId;
  const isLead = true;
  const contentType = isLead ? "leads" : "patients";

  const fetchNotes = useCallback(async () => {
    if (!effectiveId) {
      setError("No patient ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const getNotes = isLead ? getLeadNotes : getPatientNotes;
      const fetchedNotes = await getNotes(effectiveId);
      console.error("is lead?", isLead);
      setNotes(fetchedNotes);
      setLoading(false);
      
      // Update parent component with count
      if (onCountChange) {
        onCountChange(fetchedNotes.length);
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
      setError("Failed to fetch notes. Please try again later.");
      setLoading(false);
      
      // Update parent component with count (0 on error)
      if (onCountChange) {
        onCountChange(0);
      }
    }
  }, [effectiveId, isLead]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Handle external control of note form
  useEffect(() => {
    setShowNoteCreate(showCreateForm);
  }, [showCreateForm]);

  const sortedNotes = [...notes].sort((a, b) => {
    if (a[sortField] < b[sortField]) return sortDirection === "asc" ? -1 : 1;
    if (a[sortField] > b[sortField]) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const filteredNotes = showFirstOnly ? sortedNotes.slice(0, 1) : sortedNotes;

  const handleNoteCreated = () => {
    fetchNotes();
    setShowNoteCreate(false);
    if (onNoteFormClose) {
      onNoteFormClose();
    }
  };

  const toggleNoteExpansion = (noteId) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  const isNoteExpanded = (noteId) => expandedNotes.has(noteId);

  const shouldShowExpandButton = (content) => {
    if (!content) return false;
    const lines = content.split('\n');
    return lines.length > 6 || content.length > 300;
  };

  if (loading) {
    return <div className="text-center py-8">Loading notes...</div>;
  }

  // Simple preview mode for collapsed view
  if (showFirstOnly) {
    if (filteredNotes.length === 0) {
      return (
        <div className="text-sm text-gray-500 italic">
          No notes yet
        </div>
      );
    }

    const firstNote = filteredNotes[0];
    return (
      <div className="border border-border rounded-lg p-3 bg-background">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">
            {formatDate(firstNote.created_at)} •{" "}
            {firstNote.created_by?.first_name || "Unknown"}
          </span>
        </div>
        <div
          className="ph-mask text-sm text-gray-700"
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {formatNoteContent(firstNote.content)}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 sm:px-0 lg:px-0 py-0">
      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3 text-primary hover:text-teal-800"
            onClick={() => {
              setError(null);
              fetchNotes();
            }}
          >
            <span className="text-lg">x</span>
          </button>
        </div>
      )}
      {showNoteCreate && (
        <NoteCreate
          authToken={authToken}
          contentType={contentType}
          patientId={effectiveId}
          onNoteCreated={handleNoteCreated}
        />
      )}

      {/* Sort Options */}
      {filteredNotes.length > 1 && (
        <div className="mb-4 flex items-center justify-end space-x-2">
          <span className="text-xs text-gray-500">Sort by:</span>
          <select
            value={`${sortField}_${sortDirection}`}
            onChange={(e) => {
              const [field, direction] = e.target.value.split("_");
              setSortField(field);
              setSortDirection(direction);
            }}
            className="text-xs border border-border rounded px-2 py-1 text-gray-600 bg-white"
          >
            <option value="created_at_desc">Newest First</option>
            <option value="created_at_asc">Oldest First</option>
            <option value="created_by_asc">Created By (A-Z)</option>
            <option value="created_by_desc">Created By (Z-A)</option>
          </select>
        </div>
      )}

      {/* Notes Grid */}
      <div className="space-y-4">
        {filteredNotes.length > 0 ? (
          filteredNotes.map((note) => (
            <div
              key={note.id}
              className="bg-background rounded-lg border border-border hover:border-primary hover:shadow-sm transition-all duration-200 p-4"
            >
              {/* Note Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-accent-teal-subtle flex items-center justify-center">
                      <span className="text-xs font-medium text-slate-600">
                        {(
                          note.created_by?.first_name?.[0] ||
                          note.created_by?.[0] ||
                          "?"
                        ).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {note.created_by?.first_name
                          ? `${note.created_by.first_name} ${
                              note.created_by.last_name || ""
                            }`.trim()
                          : note.created_by || "Unknown"}
                      </p>
                      <span className="text-xs text-gray-500">•</span>
                      <p className="text-xs text-gray-500">
                        {formatDate(note.created_at)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2">
                  {shouldShowExpandButton(formatNoteContent(note.content)) && (
                    <button
                      onClick={() => toggleNoteExpansion(note.id)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-gray-600 bg-accent-teal-subtle hover:bg-background hover:text-gray-800 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                      {isNoteExpanded(note.id) ? "Show Less" : "Show More"}
                    </button>
                  )}
                </div>
              </div>

              {/* Note Content */}
              <div className="relative">
                <div
                  onClick={() => {
                    if (!isNoteExpanded(note.id)) {
                      shouldShowExpandButton(formatNoteContent(note.content)) &&
                        toggleNoteExpansion(note.id);
                    }
                  }}
                  className={`ph-mask text-sm text-gray-700 leading-relaxed transition-all duration-300 ease-in-out overflow-hidden ${
                    isNoteExpanded(note.id)
                      ? "max-h-none"
                      : shouldShowExpandButton(formatNoteContent(note.content))
                        ? "max-h-20 cursor-pointer hover:bg-background"
                        : "max-h-none"
                  }`}
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                  title={
                    shouldShowExpandButton(formatNoteContent(note.content))
                      ? isNoteExpanded(note.id)
                        ? "Click to collapse"
                        : "Click to expand"
                      : ""
                  }
                >
                  {formatNoteContent(note.content)}
                </div>

                {/* Fade overlay for truncated content */}
                {shouldShowExpandButton(formatNoteContent(note.content)) &&
                  !isNoteExpanded(note.id) && (
                    <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none"></div>
                  )}
              </div>

              {/* Quick Preview Footer */}
              {shouldShowExpandButton(formatNoteContent(note.content)) &&
                !isNoteExpanded(note.id) && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 italic">
                      {formatNoteContent(note.content).length > 150
                        ? `${Math.ceil(
                            formatNoteContent(note.content).length / 100,
                          )} lines of content...`
                        : 'Click "Show More" to see full note'}
                    </p>
                  </div>
                )}
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-accent-teal-subtle rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">
              No notes yet
            </h3>
            <p className="text-xs text-gray-500">
              Start by creating your first note for this lead.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadNotes;